import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grow,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  LinearProgress,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import AddTaskIcon from "@mui/icons-material/AddTask";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import VerifiedIcon from "@mui/icons-material/Verified";
import api from "../../../../conf/api";
import { formatBytes } from "./constants";
import useS3Upload from "./useS3Upload";
import { SelectedFileCard, UploadDropzone, UploadProgress } from "./UploadPanel";

const WIZARD_STEPS = ["Brand & Details", "Add WGIDs", "Upload Video", "Review"];

/**
 * Four MUI Stepper labels don't survive a 360px screen — they overlap into
 * illegible stacks. Below sm the same information is a progress bar plus
 * "Step 2 of 4 · Add WGIDs", which says more in less room.
 */
function WizardProgress({ activeStep }) {
  return (
    <Box sx={{ mt: 1.5, mb: 2.5 }}>
      <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mb={0.75}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {WIZARD_STEPS[activeStep]}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          Step {activeStep + 1} of {WIZARD_STEPS.length}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={((activeStep + 1) / WIZARD_STEPS.length) * 100}
        sx={{
          height: 6, borderRadius: 3, bgcolor: "#e2e8f0",
          "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: "#10b981" },
        }}
      />
    </Box>
  );
}

function StepBrandDetails({ brands, brandName, setBrandName, title, setTitle, description, setDescription, keywords, setKeywords }) {
  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2.5 }} required>
        <InputLabel>Brand</InputLabel>
        <Select value={brandName} label="Brand" onChange={(e) => setBrandName(e.target.value)}>
          {(brands || []).map((b) => (
            <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth label="Title" value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 2.5 }} required
        helperText="Shown wherever this compilation is published"
      />
      <TextField
        fullWidth label="Description" multiline rows={3} value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ mb: 2.5 }}
      />
      <TextField
        fullWidth label="Keywords (comma separated)" value={keywords}
        onChange={(e) => setKeywords(e.target.value)}
      />
    </Box>
  );
}

function StepWgids({ wgidInput, setWgidInput, checkingWgid, onAdd, wgidList, onRemove }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Optional — enter one or more WooGlobe WGIDs (comma separated for several at once) to say
        this compilation was assembled from those source videos. Each is verified the moment you
        add it. Leave this empty for a plain local upload.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          autoFocus
          placeholder="Enter one or more WGIDs, comma separated, and press Enter…"
          value={wgidInput}
          onChange={(e) => setWgidInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())}
          disabled={checkingWgid}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={onAdd}
          disabled={checkingWgid || !wgidInput.trim()}
          startIcon={checkingWgid ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddIcon />}
          sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap", minHeight: 40, flexShrink: 0 }}
        >
          {checkingWgid ? "Checking…" : "Add"}
        </Button>
      </Box>

      {wgidList.length === 0 ? (
        <Box sx={{ border: "1px dashed #cbd5e1", borderRadius: 3, py: 4, textAlign: "center", color: "text.secondary" }}>
          <FactCheckIcon sx={{ fontSize: 32, opacity: 0.35, mb: 0.5 }} />
          <Typography variant="body2">No WGIDs added yet</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          {wgidList.map((w) => (
            <Grow in key={w.wgid}>
              <Paper
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5, borderColor: "#bbf7d0", bgcolor: "#f0fdf4" }}
              >
                <VerifiedIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap>{w.wgid}</Typography>
                  {w.title && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {w.title}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => onRemove(w.wgid)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            </Grow>
          ))}
        </Box>
      )}
    </Box>
  );
}

function StepReview({ brandName, title, description, keywords, wgidList, file }) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "#e2e8f0" }}>
        <Typography variant="overline" color="text.secondary">Compilation</Typography>
        <Typography variant="h6" fontWeight={700}>{title || "Untitled"}</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          <Chip size="small" icon={<BusinessIcon fontSize="small" />} label={brandName || "No brand"} />
          <Chip
            size="small"
            icon={<VerifiedIcon fontSize="small" />}
            color={wgidList.length ? "success" : "default"}
            variant={wgidList.length ? "filled" : "outlined"}
            label={`${wgidList.length} WGID${wgidList.length !== 1 ? "s" : ""}`}
          />
        </Box>
        {description && <Typography variant="body2" color="text.secondary" mt={1.5}>{description}</Typography>}
        {!!keywords.trim() && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={1.5}>
            {keywords.split(",").map((k) => k.trim()).filter(Boolean).map((k) => (
              <Chip key={k} size="small" variant="outlined" label={k} />
            ))}
          </Box>
        )}
      </Paper>

      {wgidList.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "#e2e8f0" }}>
          <Typography variant="overline" color="text.secondary">WGIDs to attach</Typography>
          <Box display="flex" flexDirection="column" gap={0.75} mt={0.5}>
            {wgidList.map((w) => (
              <Box key={w.wgid} display="flex" alignItems="center" gap={1}>
                <VerifiedIcon sx={{ color: "#16a34a", fontSize: 17 }} />
                <Typography variant="body2">{w.wgid}{w.title ? ` — ${w.title}` : ""}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, display: "flex", alignItems: "center", gap: 1.5, borderColor: "#e2e8f0" }}>
        <MovieCreationIcon sx={{ color: "#64748b" }} />
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>{file?.name || "No file selected"}</Typography>
          {file && <Typography variant="caption" color="text.secondary">{formatBytes(file.size)}</Typography>}
        </Box>
      </Paper>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        The video uploads straight to a private area of S3 and stays unreadable to anyone until an
        admin approves this compilation — at which point it's moved to its public path. Rejecting
        it deletes the upload instead.
      </Alert>
    </Box>
  );
}

/**
 * Create a compilation: brand & details → WGIDs (verified live) → upload →
 * review. The WCA id is minted server-side on creation.
 *
 * The video goes browser-direct to S3 in parallel parts (useS3Upload) rather
 * than being chunked through Django, so a multi-GB compilation is bounded by
 * the uploader's own connection instead of by an app server round trip.
 */
export default function CreateCompilationDialog({ open, brands, defaultBrand, onClose, onCreated }) {
  const [activeStep, setActiveStep] = useState(0);
  const [brandName, setBrandName] = useState(defaultBrand || "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");

  const [wgidInput, setWgidInput] = useState("");
  const [checkingWgid, setCheckingWgid] = useState(false);
  const [wgidList, setWgidList] = useState([]); // [{ wgid, title }]

  const [file, setFile] = useState(null);
  const [creating, setCreating] = useState(false);

  const upload = useS3Upload();
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open) {
      setBrandName(defaultBrand || "");
      setActiveStep(0);
    }
  }, [open, defaultBrand]);

  const reset = () => {
    setTitle(""); setDescription(""); setKeywords("");
    setWgidInput(""); setWgidList([]); setFile(null); setActiveStep(0);
    upload.reset();
  };

  const busy = creating || upload.isBusy;

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  // Live validation against the same WooGlobe endpoint used to resolve a
  // video during publishing. Accepts one WGID or several comma-separated at
  // once — each verified concurrently, only the ones that check out get added.
  const handleAddWgid = async () => {
    const candidates = Array.from(new Set(wgidInput.split(",").map((w) => w.trim()).filter(Boolean)));
    if (candidates.length === 0) return;

    const alreadyInList = candidates.filter((w) => wgidList.some((existing) => existing.wgid === w));
    const toCheck = candidates.filter((w) => !alreadyInList.includes(w));
    if (alreadyInList.length > 0) toast.info(`${alreadyInList.join(", ")} already in the list`);
    if (toCheck.length === 0) {
      setWgidInput("");
      return;
    }

    setCheckingWgid(true);
    try {
      const results = await Promise.all(
        toCheck.map(async (wgid) => {
          try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE + `/apis/VideoDetail/${wgid}`);
            if (response.data && response.data.data) {
              return { ok: true, wgid, title: response.data.data.title || "" };
            }
            return { ok: false, wgid };
          } catch {
            return { ok: false, wgid };
          }
        })
      );
      const verified = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      if (verified.length > 0) {
        setWgidList((prev) => [...prev, ...verified.map(({ wgid, title: t }) => ({ wgid, title: t }))]);
      }
      if (failed.length > 0) toast.error(`Not found: ${failed.map((f) => f.wgid).join(", ")}`);
      setWgidInput("");
    } finally {
      setCheckingWgid(false);
    }
  };

  const handleRemoveWgid = (wgid) => setWgidList((prev) => prev.filter((w) => w.wgid !== wgid));

  const stepValid = [!!brandName && !!title.trim(), true, !!file, true][activeStep];
  const isLastStep = activeStep === WIZARD_STEPS.length - 1;

  const handleCreate = async () => {
    setCreating(true);
    try {
      // Upload first — a compilation row is only created once its video is
      // actually staged on S3, so a failed upload can't leave a row pointing
      // at a file that never arrived.
      const stagedKey = await upload.start(file, "video");
      const res = await api.post("/mrss/compilations/", {
        brand: brandName,
        title: title.trim(),
        description,
        keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        staged_video_key: stagedKey,
        wgids: wgidList.map((w) => w.wgid),
      });
      const wcaId = res.data?.unique_key;
      const failed = res.data?.wgid_results?.failed || [];
      toast.success(wcaId ? `Compilation created — ${wcaId}` : "Compilation created");
      if (failed.length > 0) {
        toast.warning(
          `${failed.length} WGID(s) failed re-verification and were not attached: ${failed.map((f) => f.wgid).join(", ")}`
        );
      }
      reset();
      onCreated && onCreated();
    } catch (err) {
      // An upload failure already shows itself in the progress panel; only
      // surface a toast for something the panel can't explain.
      if (upload.state.phase !== "error" && upload.state.phase !== "cancelled") {
        toast.error(err.response?.data?.error || err.message || "Failed to create compilation");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ pb: 0, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AddTaskIcon sx={{ color: "#059669", flexShrink: 0 }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: 17, sm: 20 } }}>
            Create Compilation
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        {fullScreen ? (
          <WizardProgress activeStep={activeStep} />
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 3 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {activeStep === 0 && (
          <StepBrandDetails
            brands={brands} brandName={brandName} setBrandName={setBrandName}
            title={title} setTitle={setTitle}
            description={description} setDescription={setDescription}
            keywords={keywords} setKeywords={setKeywords}
          />
        )}
        {activeStep === 1 && (
          <StepWgids
            wgidInput={wgidInput} setWgidInput={setWgidInput}
            checkingWgid={checkingWgid} onAdd={handleAddWgid}
            wgidList={wgidList} onRemove={handleRemoveWgid}
          />
        )}
        {activeStep === 2 && (
          file
            ? <SelectedFileCard file={file} onRemove={() => setFile(null)} disabled={busy} />
            : <UploadDropzone onFile={setFile} />
        )}
        {activeStep === 3 && (
          <StepReview
            brandName={brandName} title={title} description={description}
            keywords={keywords} wgidList={wgidList} file={file}
          />
        )}

        <UploadProgress state={upload.state} onCancel={upload.cancel} onRetry={handleCreate} />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, pt: 1.5, gap: 1, flexWrap: "wrap",
          borderTop: { xs: "1px solid #eef2f6", sm: "none" },
          "& .MuiButton-root": { minHeight: 40, ml: "0 !important" },
        }}
      >
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: "none" }}>Cancel</Button>
        <Box flex={1} />
        {activeStep > 0 && (
          <Button
            variant="outlined" color="inherit" startIcon={<ArrowBackIcon />}
            onClick={() => setActiveStep((s) => s - 1)} disabled={busy}
            sx={{ borderRadius: 2, textTransform: "none", borderColor: "#e2e8f0" }}
          >
            Back
          </Button>
        )}
        {!isLastStep ? (
          <Button
            variant="contained" disableElevation color="success" endIcon={<ArrowForwardIcon />}
            onClick={() => setActiveStep((s) => s + 1)} disabled={!stepValid}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained" disableElevation color="success"
            onClick={handleCreate} disabled={busy || !file || !title.trim() || !brandName}
            endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {upload.isBusy ? "Uploading…" : creating ? "Creating…" : "Create Compilation"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
